import {useState} from 'react';
import {MoreHorizontal, Plus} from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Field,
  FieldLabel,
  Input
} from '@kroll-resolver-crisp/ui-components-react';
import {getApiErrorMessage} from '@/api/client';
import {type PolicyViolationQueueSummary} from '@/api/moderationQueue';
import {useCreateSavedView, useDeleteSavedView, useUpdateSavedView} from '@/api/savedViewHooks';
import {SAVED_VIEW_NAME_MAX_LENGTH} from '@/api/savedViews';
import {queueGroupCount, type QueueGroup, type QueueGroupSelection} from '@/lib/queueGroups';
import {showToast} from '@/lib/toast';
import {cn} from '@/lib/utils';

export type GroupDraft = {
  readonly id: string | null;
  readonly selections: readonly QueueGroupSelection[];
};

type Props = {
  readonly groups: readonly QueueGroup[];
  readonly summaries: readonly PolicyViolationQueueSummary[];
  readonly activeId: string | null;
  // Picks a group, or null for none.
  readonly onApply: (id: string | null) => void;
  // The group being built in the verticals tree below; null when none is.
  readonly draft: GroupDraft | null;
  readonly onDraft: (draft: GroupDraft | null) => void;
};

// The analyst's queue groups, above the verticals tree in the queue's navigation: named sets of verticals and
// taxonomy nodes claimed from as one queue. Picking a group selects it like a tree row. New and Edit turn the
// tree into checkboxes, with Save and Cancel here, until the draft is saved or dropped.
export function QueueGroupsSection({groups, summaries, activeId, onApply, draft, onDraft}: Props) {
  const {mutate: create, isPending: creating} = useCreateSavedView('queue');
  const {mutate: update, isPending: updating} = useUpdateSavedView('queue');
  const {mutate: remove} = useDeleteSavedView('queue');
  // The group being renamed, or 'new' while naming a new one.
  const [naming, setNaming] = useState<QueueGroup | 'new' | null>(null);
  const [name, setName] = useState('');
  const drafting = groups.find((group) => group.id === draft?.id);

  const fail = (fallback: string) => (error: unknown) => {
    showToast(getApiErrorMessage(error, fallback), 'error');
  };

  const saveDraft = () => {
    if (!draft) {
      return;
    }

    if (!drafting) {
      setName('');
      setNaming('new');
      return;
    }

    update(
      {id: drafting.id, query: {selections: draft.selections}},
      {
        onSuccess() {
          onDraft(null);
          onApply(drafting.id);
        },
        onError: fail('Failed to update the group.')
      }
    );
  };

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed || naming === null) {
      return;
    }

    if (naming !== 'new') {
      update(
        {id: naming.id, name: trimmed},
        {
          onSuccess() {
            setNaming(null);
          },
          onError: fail('Failed to rename the group.')
        }
      );
      return;
    }

    create(
      {name: trimmed, query: {selections: draft?.selections ?? []}},
      {
        onSuccess(view) {
          setNaming(null);
          onDraft(null);
          onApply(view.id);
          showToast(`Saved group "${view.name}".`, 'success');
        },
        onError: fail('Failed to save the group.')
      }
    );
  };

  return (
    <section aria-label="Groups" className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <h2 className="text-xs font-semibold">Groups</h2>
        {draft ? null : (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="New group"
            title="New group"
            onClick={() => {
              onDraft({id: null, selections: []});
            }}>
            <Plus />
          </Button>
        )}
      </div>

      {draft ? (
        <div className="border-border bg-muted/40 flex flex-col gap-2 rounded-md border p-2 text-xs">
          <p>
            <span className="font-medium">
              {drafting ? `Editing “${drafting.name}”` : 'New group'}
            </span>
            <span className="text-muted-foreground">
              {' '}
              · {queueGroupCount(summaries, draft.selections)} in pool
            </span>
          </p>
          <p className="text-muted-foreground">
            Tick verticals and classifications below. A classification includes everything under it.
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              disabled={draft.selections.length === 0 || updating}
              onClick={saveDraft}>
              Save group
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                onDraft(null);
              }}>
              Cancel
            </Button>
          </div>
        </div>
      ) : groups.length === 0 ? (
        <p className="text-muted-foreground px-1 text-xs">
          Save classifications from any vertical as a group to work them as one queue.
        </p>
      ) : (
        <ul className="flex flex-col">
          {groups.map((group) => {
            const selected = group.id === activeId;
            return (
              <li
                key={group.id}
                className={cn(
                  'group/row hover:bg-muted flex items-center gap-1 pr-1 text-xs',
                  selected && 'bg-muted font-medium'
                )}>
                <button
                  type="button"
                  aria-pressed={selected}
                  className="focus-visible:ring-ring flex min-w-0 flex-1 items-center gap-1 py-1 pl-5 text-left outline-none focus-visible:ring-1"
                  onClick={() => {
                    onApply(selected ? null : group.id);
                  }}>
                  <span className="min-w-0 flex-1 truncate" title={group.name}>
                    {group.name}
                  </span>
                  <Badge
                    variant={selected ? 'default' : 'secondary'}
                    className="shrink-0 tabular-nums">
                    {queueGroupCount(summaries, group.selections)}
                  </Badge>
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        aria-label={`Actions for ${group.name}`}
                        className="opacity-60 group-hover/row:opacity-100 focus-visible:opacity-100">
                        <MoreHorizontal />
                      </Button>
                    }
                  />
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => {
                        onDraft({id: group.id, selections: group.selections});
                      }}>
                      Edit classifications
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        setName(group.name);
                        setNaming(group);
                      }}>
                      Rename…
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      variant="destructive"
                      onClick={() => {
                        remove(group.id, {
                          onSuccess() {
                            if (selected) {
                              onApply(null);
                            }

                            showToast(`Deleted group "${group.name}".`, 'success');
                          },
                          onError: fail('Failed to delete the group.')
                        });
                      }}>
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </li>
            );
          })}
        </ul>
      )}

      <Dialog
        open={naming !== null}
        onOpenChange={(open) => {
          if (!open) {
            setNaming(null);
          }
        }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{naming === 'new' ? 'Save group' : 'Rename group'}</DialogTitle>
            <DialogDescription>Only you see your groups.</DialogDescription>
          </DialogHeader>
          <form
            id="queue-group-form"
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}>
            <Field>
              <FieldLabel htmlFor="queue-group-name">Name</FieldLabel>
              <Input
                id="queue-group-name"
                value={name}
                maxLength={SAVED_VIEW_NAME_MAX_LENGTH}
                placeholder="e.g. Hate and harassment"
                onChange={(event) => {
                  setName(event.target.value);
                }}
              />
            </Field>
          </form>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setNaming(null);
              }}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="queue-group-form"
              disabled={!name.trim() || creating || updating}>
              {naming === 'new' ? 'Save group' : 'Rename'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
